import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, Landmark } from "lucide-react";
import { useCart, useAuth } from "../lib/store";
import { InvoiceFields, EMPTY_INVOICE, invoiceValid } from "../components/InvoiceFields";
import { api } from "../lib/api";
import { toast } from "sonner";
import { SectionHead } from "./Home";
import { TermsCheckbox } from "../components/TermsCheckbox";
import { VoucherBox } from "../components/VoucherBox";

const BANK = {
  intestatario: "Andrea Giacomelli",
  iban: "IT93I0366901600506384833012",
  causale_prefix: "PS",
};

const SHIPPING_OPTIONS = [
  {
    v: "vinted",
    l: "Vinted",
    cost: 0,
    costLabel: "Paghi su Vinted",
    note: "La spedizione viene pagata direttamente sulla piattaforma Vinted, non qui.",
  },
  {
    v: "brt",
    l: "BRT 24/48h",
    cost: 10,
    costLabel: "€ 10,00",
    note: "Corriere privato BRT, consegna in 24-48h con tracking.",
  },
  {
    v: "brt-insured",
    l: "BRT 24/48h Assicurata",
    cost: 30,
    costLabel: "€ 30,00",
    note: "BRT con assicurazione fino a € 2.500. Consigliato per box, case, graded e ordini di alto valore.",
  },
];

export default function Checkout() {
  const { items, subtotal, balance, clear, effectivePrice } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [invoice, setInvoice] = useState(EMPTY_INVOICE);

  const [form, setForm] = useState({
    shipping_name: "",
    shipping_address: "",
    shipping_city: "",
    shipping_zip: "",
    shipping_country: "IT",
    shipping_method: "vinted",
    payment_method: "bonifico-istantaneo",
  });

  const selectedShipping = SHIPPING_OPTIONS.find((s) => s.v === form.shipping_method) || SHIPPING_OPTIONS[0];
  const shipping = selectedShipping.cost;
  const [voucher, setVoucher] = useState(null);
  const discount = voucher ? Math.min(voucher.balance, subtotal) : 0;
  const total = subtotal - discount + shipping;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        ...form,
        accept_terms: accepted,
        voucher_code: voucher?.code || null,
        customer_email: user ? null : email,
        wants_invoice: wantsInvoice,
        invoice: wantsInvoice ? invoice : null,
      });
      setConfirmed(data);
      clear();
      toast.success("Ordine confermato!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore checkout");
    } finally {
      setBusy(false);
    }
  };

  if (confirmed) {
    const causale = `${BANK.causale_prefix}-${confirmed.order_id.slice(-8).toUpperCase()}`;
    const copy = (txt) => {
      navigator.clipboard?.writeText(txt);
      toast.success("Copiato negli appunti");
    };
    return (
      <div className="max-w-3xl mx-auto px-4 py-14">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full border border-[#C9A227] flex items-center justify-center text-[#C9A227] mb-6">
            <Check size={28} />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white">Ordine ricevuto</h1>
          <p className="text-[#9CA3AF] mt-2">
            Ordine <span className="font-mono-ps text-[#C9A227]">#{confirmed.order_id.slice(-8).toUpperCase()}</span> — Totale <span className="text-[#C9A227] font-mono-ps">€ {confirmed.total.toFixed(2)}</span>
          </p>
        </div>

        {confirmed.total === 0 ? (
          <div data-testid="paid-by-voucher" className="mt-8 border border-emerald-500/50 bg-emerald-500/5 rounded-sm p-6 text-center">
            <div className="font-mono-ps text-xs tracking-widest uppercase text-emerald-400">Pagato interamente con buono negozio</div>
            <p className="text-sm text-[#D1D5DB] mt-3">Non devi effettuare nessun bonifico: il tuo ordine è confermato e lo prepariamo a breve.</p>
          </div>
        ) : (
        <div className="mt-8 border border-[#C9A227] bg-[#C9A227]/5 rounded-sm p-6">
          <div className="flex items-center gap-2 text-[#C9A227]">
            <Landmark size={18} />
            <span className="font-mono-ps text-xs tracking-widest uppercase">Completa l'ordine con bonifico istantaneo</span>
          </div>
          <p className="text-sm text-[#D1D5DB] mt-3 leading-relaxed">
            Effettua un bonifico bancario istantaneo con i dati qui sotto.
            Non appena riceviamo il pagamento (di solito entro pochi minuti) processeremo il tuo ordine
            e riceverai conferma sul gruppo WhatsApp o via contatto diretto.
          </p>

          <div className="mt-5 space-y-2">
            <BankRow label="Intestatario" value={BANK.intestatario} onCopy={() => copy(BANK.intestatario)} />
            <BankRow label="IBAN" value={BANK.iban} mono onCopy={() => copy(BANK.iban)} />
            <BankRow label="Importo" value={`€ ${confirmed.total.toFixed(2)}`} mono onCopy={() => copy(confirmed.total.toFixed(2))} />
            <BankRow label="Causale (importante)" value={causale} mono highlight onCopy={() => copy(causale)} />
          </div>

          <p className="text-[11px] font-mono-ps text-[#9CA3AF] mt-4 leading-relaxed">
            ⚠️ Inserisci esattamente la causale <span className="text-[#C9A227]">{causale}</span> nel bonifico — ci serve per abbinare velocemente il pagamento al tuo ordine.
          </p>
        </div>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button onClick={() => navigate("/")} className="btn-ghost-gold">Torna alla home</button>
          <button onClick={() => navigate("/account")} data-testid="view-orders" className="btn-gold">I miei ordini</button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h2 className="font-display text-2xl text-white">Il carrello è vuoto</h2>
        <button onClick={() => navigate("/")} className="btn-gold mt-6">Torna allo shop</button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 md:py-14">
      <SectionHead eyebrow="Checkout" title="Completa il tuo ordine" />

      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Step n={1} label="Spedizione" active={step === 1} done={step > 1} onClick={() => setStep(1)}>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="ck-name" label="Nome completo" value={form.shipping_name} onChange={(v) => setForm({ ...form, shipping_name: v })} />
              <Input data-testid="ck-address" label="Indirizzo" value={form.shipping_address} onChange={(v) => setForm({ ...form, shipping_address: v })} />
              <Input data-testid="ck-city" label="Città" value={form.shipping_city} onChange={(v) => setForm({ ...form, shipping_city: v })} />
              <Input data-testid="ck-zip" label="CAP" value={form.shipping_zip} onChange={(v) => setForm({ ...form, shipping_zip: v })} />
              {!user && (
                <Input data-testid="ck-email" label="Email (ti inviamo qui la ricevuta)" value={email} onChange={setEmail} />
              )}
            </div>

            <div className="mt-6">
              <div className="text-[10px] font-mono-ps uppercase tracking-widest text-[#C9A227] mb-2">Scegli il corriere</div>
              <div className="space-y-2">
                {SHIPPING_OPTIONS.map((s) => (
                  <label
                    key={s.v}
                    className={`flex items-start justify-between border rounded-sm p-4 cursor-pointer transition-colors ${
                      form.shipping_method === s.v ? "border-[#C9A227] bg-[#C9A227]/5" : "border-[#C9A227]/20 hover:border-[#C9A227]/50"
                    }`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <input
                        type="radio"
                        name="ship"
                        checked={form.shipping_method === s.v}
                        onChange={() => setForm({ ...form, shipping_method: s.v })}
                        className="accent-[#C9A227] mt-1"
                        data-testid={`ship-${s.v}`}
                      />
                      <div className="min-w-0">
                        <div className="text-white font-semibold">{s.l}</div>
                        <div className="text-xs text-[#9CA3AF] mt-0.5 leading-relaxed">{s.note}</div>
                      </div>
                    </div>
                    <div className={`font-mono-ps font-bold shrink-0 ml-3 ${s.cost === 0 ? "text-[#9CA3AF] text-xs uppercase tracking-widest" : "text-[#C9A227]"}`}>
                      {s.costLabel}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <button
                data-testid="ck-next-1"
                onClick={() => setStep(2)}
                disabled={!form.shipping_name || !form.shipping_address || !form.shipping_city || !form.shipping_zip || (!user && !/^\S+@\S+\.\S+$/.test(email))}
                className="btn-gold disabled:opacity-40"
              >
                Continua
              </button>
            </div>
          </Step>

          <Step n={2} label="Pagamento" active={step === 2} done={step > 2} onClick={() => setStep(2)}>
            <div className="border border-[#C9A227] rounded-sm p-5 bg-[#C9A227]/5">
              <div className="flex items-center gap-2 text-[#C9A227]">
                <Landmark size={18} />
                <span className="font-mono-ps text-xs tracking-widest uppercase">Bonifico bancario istantaneo</span>
              </div>
              <p className="text-sm text-[#D1D5DB] mt-3 leading-relaxed">
                Al momento accettiamo esclusivamente bonifico bancario istantaneo.
                Riceverai i dati bancari e la causale personalizzata subito dopo la conferma dell'ordine.
              </p>
              <ul className="mt-3 space-y-1.5 text-xs text-[#9CA3AF]">
                <li className="flex items-center gap-2"><span className="text-[#C9A227]">▸</span> Pagamento tracciabile e istantaneo</li>
                <li className="flex items-center gap-2"><span className="text-[#C9A227]">▸</span> Nessuna commissione aggiuntiva</li>
                <li className="flex items-center gap-2"><span className="text-[#C9A227]">▸</span> Ordine processato appena riceviamo il bonifico</li>
              </ul>
            </div>
            <InvoiceFields enabled={wantsInvoice} setEnabled={setWantsInvoice} value={invoice} onChange={setInvoice} />
            <div className="mt-4 flex gap-2">
              <button onClick={() => setStep(1)} className="btn-ghost-gold">Indietro</button>
              <button data-testid="ck-next-2" onClick={() => setStep(3)} disabled={wantsInvoice && !invoiceValid(invoice)} className="btn-gold disabled:opacity-40">Rivedi ordine</button>
            </div>
          </Step>

          <Step n={3} label="Conferma" active={step === 3} done={false} onClick={() => setStep(3)}>
            <div className="space-y-3 text-sm">
              <div><span className="text-[#9CA3AF]">Spedizione a:</span> <span className="text-white">{form.shipping_name}, {form.shipping_address}, {form.shipping_city} {form.shipping_zip}</span></div>
              <div><span className="text-[#9CA3AF]">Corriere:</span> <span className="text-white">{selectedShipping.l}</span> — <span className="text-[#C9A227] font-mono-ps">{shipping === 0 ? "paghi su Vinted" : `€ ${shipping.toFixed(2)}`}</span></div>
              <div><span className="text-[#9CA3AF]">Pagamento:</span> <span className="text-white">Bonifico bancario istantaneo</span></div>
              <div><span className="text-[#9CA3AF]">Documento:</span> <span data-testid="ck-doc-summary" className="text-white">{wantsInvoice ? `Fattura elettronica a ${invoice.company_name}` : "Ricevuta PDF via email"}</span></div>
              <div className="text-xs text-[#9CA3AF] mt-1">Al click qui sotto riceverai IBAN e causale per completare il pagamento.</div>
            </div>
            <div className="mt-4"><TermsCheckbox checked={accepted} onChange={setAccepted} testid="ck-accept-terms" /></div>
            <button data-testid="ck-confirm" onClick={submit} disabled={busy || !accepted} className="btn-gold mt-4 disabled:opacity-60">
              {busy ? "Conferma…" : `Conferma ordine (€ ${total.toFixed(2)})`}
            </button>
          </Step>
        </div>

        {/* Order summary */}
        <aside className="border border-[#C9A227]/20 rounded-sm p-5 h-fit bg-[#0b0b0d] sticky top-24">
          <div className="eyebrow mb-3">Riepilogo</div>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="text-white/85 pr-3 line-clamp-2">
                  {i.name} <span className="text-[#9CA3AF] font-mono-ps">× {i.qty}</span>
                  {i.preorder_type === "allocation-deposit" && (
                    <span className="block text-[10px] text-[#C9A227] font-mono-ps tracking-widest uppercase mt-0.5">Acconto {i.deposit_percent}%</span>
                  )}
                </span>
                <span className="font-mono-ps text-white shrink-0">€ {(effectivePrice(i) * i.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-[#C9A227]/15 space-y-2 text-sm">
            <div className="flex justify-between text-[#9CA3AF]"><span>Subtotale</span><span className="font-mono-ps text-white">€ {subtotal.toFixed(2)}</span></div>
            <VoucherBox voucher={voucher} onApply={setVoucher} onRemove={() => setVoucher(null)} />
            {discount > 0 && (
              <div data-testid="voucher-discount" className="flex justify-between text-emerald-400">
                <span>Buono negozio</span><span className="font-mono-ps">− € {discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-[#9CA3AF]">
              <span>Spedizione ({selectedShipping.l})</span>
              <span className="font-mono-ps text-white">
                {shipping === 0 ? "Paghi su Vinted" : `€ ${shipping.toFixed(2)}`}
              </span>
            </div>
            {balance > 0 && (
              <div className="flex justify-between text-[#C9A227] pt-2 border-t border-[#C9A227]/10">
                <span>Saldo alla conferma</span><span className="font-mono-ps">€ {balance.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-[#C9A227]/15 font-display font-bold"><span className="text-white">Totale oggi</span><span className="text-[#C9A227] font-mono-ps">€ {total.toFixed(2)}</span></div>
          </div>
          {balance > 0 && (
            <p className="text-[10px] text-[#9CA3AF] mt-3 leading-relaxed">
              Il saldo per i prodotti su allocazione verrà richiesto separatamente quando ci confermeranno le quantità assegnate.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}

const Step = ({ n, label, active, done, onClick, children }) => (
  <div className={`border rounded-sm ${active ? "border-[#C9A227]" : "border-[#C9A227]/15"} bg-[#0b0b0d]`}>
    <button onClick={onClick} className="w-full flex items-center gap-3 px-5 py-4 text-left">
      <span className={`w-7 h-7 rounded-sm flex items-center justify-center text-xs font-mono-ps ${done ? "bg-[#C9A227] text-black" : active ? "border border-[#C9A227] text-[#C9A227]" : "border border-[#C9A227]/30 text-[#9CA3AF]"}`}>
        {done ? <Check size={14} /> : n}
      </span>
      <span className={`font-display font-semibold ${active ? "text-white" : "text-[#9CA3AF]"}`}>{label}</span>
    </button>
    {active && <div className="px-5 pb-5">{children}</div>}
  </div>
);

const Input = ({ label, value, onChange, ...rest }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">{label}</span>
    <input {...rest} value={value} onChange={(e) => onChange(e.target.value)}
      className="bg-black border border-[#C9A227]/25 focus:border-[#C9A227] rounded-sm px-3 py-2.5 text-white outline-none text-base"/>
  </label>
);

const BankRow = ({ label, value, mono, highlight, onCopy }) => (
  <div className={`flex items-center justify-between gap-3 border rounded-sm px-4 py-3 ${
    highlight ? "border-[#C9A227] bg-[#C9A227]/10" : "border-[#C9A227]/20 bg-black"
  }`}>
    <div className="min-w-0">
      <div className="text-[10px] font-mono-ps uppercase tracking-widest text-[#9CA3AF]">{label}</div>
      <div className={`mt-1 truncate ${mono ? "font-mono-ps" : ""} ${highlight ? "text-[#C9A227] font-bold" : "text-white"}`}>{value}</div>
    </div>
    <button
      type="button"
      onClick={onCopy}
      data-testid={`copy-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      className="p-2 border border-[#C9A227]/40 hover:border-[#C9A227] hover:bg-[#C9A227]/10 text-[#C9A227] rounded-sm transition-colors shrink-0"
      title="Copia"
    >
      <Copy size={14} />
    </button>
  </div>
);
